import { computed, ref, watch, type Ref } from 'vue'

import { type AccountSearchResponse, type PostListResponse } from '@/typings/twitter_benchmark_campaign_posts'

import { searchTwBenchmarkAccounts } from '../api/searchTwBenchmarkAccounts'

type Account = AccountSearchResponse['accounts'][number]
type Post = PostListResponse['posts'][number]

const ACCOUNT_SEARCH_MIN_LENGTH = 2

export type UseAccountSelectResolverOptions = {
  accountIds: Ref<number[]>
  groupId: Ref<number | undefined>
  posts: Ref<Post[]>
}

/**
 * アカウント検索 el-select の選択肢解決フック
 */
export function useAccountSelectResolver({ accountIds, groupId, posts }: UseAccountSelectResolverOptions) {
  const accountSearchResults = ref<Account[]>([])
  const selectedAccounts = ref<Account[]>([])
  const accountLoading = ref(false)
  const accountQuery = ref('')

  const accountNoDataText = computed(() =>
    accountQuery.value.length === 1 ? '2文字以上を入力してください' : '検索結果がありません'
  )

  const accountOptions = computed<Account[]>(() => {
    const additional = accountSearchResults.value.filter(
      account => !selectedAccounts.value.some(selected => selected.id === account.id)
    )
    return [...selectedAccounts.value, ...additional]
  })

  /**
   * 選択済みアカウントを accountIds に合わせて再構成する
   */
  const syncSelectedAccounts = (candidates: Account[]): void => {
    const resolvedCandidates = [...selectedAccounts.value, ...candidates]
    selectedAccounts.value = accountIds.value
      .map(id => resolvedCandidates.find(account => account.id === id))
      .filter((account): account is Account => account !== undefined)
  }

  const searchAccounts = async (query: string): Promise<void> => {
    accountQuery.value = query
    const screenName = query.trim()
    if (screenName.length < ACCOUNT_SEARCH_MIN_LENGTH || typeof groupId.value !== 'number') {
      accountSearchResults.value = []
      return
    }

    accountLoading.value = true
    const result = await searchTwBenchmarkAccounts({ groupId: groupId.value, screenName })
    accountLoading.value = false
    if (result.isSuccess && result.data) {
      const { accounts } = result.data
      accountSearchResults.value = accounts
      syncSelectedAccounts(accounts)
    }
  }

  const resolveUnknownAccountIds = async (): Promise<void> => {
    syncSelectedAccounts(accountSearchResults.value)

    const resolvedAccounts = [...selectedAccounts.value, ...accountSearchResults.value]
    const unknownIds = accountIds.value.filter(id => !resolvedAccounts.some(account => account.id === id))
    if (!unknownIds.length) return

    const fromPosts = posts.value
      .map(item => item.account)
      .filter((account): account is NonNullable<Post['account']> => account !== null)
      .filter(account => unknownIds.includes(account.id))
      .map(({ id, screenName, name, pictureUrl }): Account => ({ id, screenName, name, pictureUrl }))
    syncSelectedAccounts(fromPosts)

    const stillUnknownIds = unknownIds.filter(id => !fromPosts.some(account => account.id === id))
    if (!stillUnknownIds.length || typeof groupId.value !== 'number') return

    const result = await searchTwBenchmarkAccounts({ groupId: groupId.value, accountIds: stillUnknownIds })
    if (result.isSuccess && result.data) {
      syncSelectedAccounts(result.data.accounts)
    }
  }

  const onAccountVisibleChange = (visible: boolean): void => {
    if (visible) {
      accountSearchResults.value = []
      syncSelectedAccounts([])
    }
  }

  watch(accountIds, resolveUnknownAccountIds)

  return {
    accountOptions,
    accountLoading,
    accountNoDataText,
    searchAccounts,
    resolveUnknownAccountIds,
    onAccountVisibleChange
  }
}
